import React, { useMemo, useState } from "react";
import { Tabs, Input, Checkbox, List, Space, Typography, Flex, Form } from "antd";
import type { TabsProps, FormInstance } from "antd";
import { ISupplierItemResult, IUserResult } from "../../interfaces/user";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import "./permission.scss";

type Supplier = { value: string; label: string };
type TabKey = "productHead" | "productOwner" | "apOwner" | "apEscalation";

interface TabSelectSupplierProps {
  detailUser: IUserResult | undefined;
  fieldName?: string;
  form?: FormInstance;
  apEscalation: ISupplierItemResult[];
  apOwner: ISupplierItemResult[];
  productHead: ISupplierItemResult[];
  productOwner: ISupplierItemResult[];
}

const TabSelectSupplier: React.FC<TabSelectSupplierProps> = ({
  detailUser,
  fieldName = "tabSuppliers",
  form: injectedForm,
  apEscalation,
  apOwner,
  productHead,
  productOwner,
}) => {
  const ctxForm = Form.useFormInstance?.();
  const form = injectedForm || ctxForm;

  // Define which tabs are visible based on user roles
  const baseItems: Array<{ key: TabKey; label: string; isShow?: boolean }> = [
    { key: "productHead", label: "Product Head", isShow: detailUser?.productHead },
    { key: "productOwner", label: "Product Owner", isShow: detailUser?.productOwner },
    { key: "apOwner", label: "AP Owner", isShow: detailUser?.apOwner },
    { key: "apEscalation", label: "AP Escalator", isShow: detailUser?.apEscalation },
  ];

  // First visible tab will be used as default active tab
  const firstVisibleTab = baseItems.find((item) => item.isShow)?.key ?? "productHead";
  const [activeKey, setActiveKey] = useState<TabKey>(firstVisibleTab);

  // Search text is only used for filtering UI, not for selection logic
  const [searchText, setSearchText] = useState<Record<TabKey, string>>({
    productHead: "",
    productOwner: "",
    apOwner: "",
    apEscalation: "",
  });

  // Supplier list by tab
  const supplierListByTab: Record<TabKey, ISupplierItemResult[]> = {
    productHead,
    productOwner,
    apOwner,
    apEscalation,
  };

  // Single source of truth: all selected suppliers are stored in AntD Form
  const allSelections = Form.useWatch(fieldName, form) || {};

  const items: TabsProps["items"] = baseItems
    .filter((item) => item.isShow)
    .map((tab) => {
      const rawSuppliers = supplierListByTab[tab.key] || [];

      // Full supplier list for this tab
      const allSuppliers: Supplier[] = rawSuppliers
        .sort((a, b) => a.supplier_code.localeCompare(b.supplier_code, "en", { sensitivity: "base" }))
        .map((s) => ({
          value: s.supplier_code,
          label: `${s.supplier_code} - ${s.name}`,
        }));

      // Selected supplier codes for this tab
      const selectedInTab: string[] = allSelections?.[tab.key] || [];

      // Filter suppliers only for UI display
      const q = (searchText[tab.key] || "").toLowerCase().trim();
      const filteredSuppliers = q
        ? allSuppliers.filter((s) => s.label.toLowerCase().includes(q) || s.value.toLowerCase().includes(q))
        : allSuppliers;

      const filteredValues = filteredSuppliers.map((s) => s.value);

      // Select All must be calculated based on the full dataset, not the filtered list
      const allValues = allSuppliers.map((s) => s.value);

      const allChecked = selectedInTab.length === allValues.length && allValues.length > 0;
      const indeterminate = selectedInTab.length > 0 && selectedInTab.length < allValues.length;

      return {
        key: tab.key,
        label: (
          <Flex align="center" gap={6} vertical>
            <span>{tab.label}</span>
            <Typography.Text>
              ({selectedInTab.length}/{allValues.length})
            </Typography.Text>
          </Flex>
        ),
        children: (
          <Space direction="vertical" size="middle" style={{ display: "flex" }}>
            <Flex wrap="wrap" gap="small" align="center" justify="space-between">
              <Input
                allowClear
                prefix={<FontAwesomeIcon icon={faSearch} />}
                placeholder="Search suppliers"
                style={{ maxWidth: 400 }}
                value={searchText[tab.key]}
                onChange={(e) => setSearchText((prev) => ({ ...prev, [tab.key]: e.target.value }))}
              />

              <Checkbox
                checked={allChecked}
                indeterminate={indeterminate}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Select all suppliers in this tab (ignore current filter)
                    form.setFieldValue([fieldName, tab.key], allValues);
                  } else {
                    // Deselect all suppliers in this tab
                    form.setFieldValue([fieldName, tab.key], []);
                  }
                }}
              >
                {allChecked ? "Deselect all" : "Select all"}
              </Checkbox>
            </Flex>

            <Form.Item name={[fieldName, tab.key]} initialValue={[]}>
              <Checkbox.Group
                value={filteredValues.filter((v) => selectedInTab.includes(v))}
                onChange={(checkedInFiltered) => {
                  const prev = selectedInTab || [];

                  // Keep all selections that are NOT in the filtered list
                  const kept = prev.filter((v) => !filteredValues.includes(v));

                  // Add all newly checked values in the filtered list
                  const merged = [...kept, ...checkedInFiltered];

                  form.setFieldValue([fieldName, tab.key], merged);
                }}
                style={{ width: "100%" }}
              >
                <List
                  dataSource={filteredSuppliers}
                  rowKey="value"
                  style={{ width: "100%", maxHeight: 200, overflow: "auto" }}
                  renderItem={(item) => (
                    <List.Item style={{ border: "none" }}>
                      <Checkbox value={item.value}>{item.label}</Checkbox>
                    </List.Item>
                  )}
                />
              </Checkbox.Group>
            </Form.Item>
          </Space>
        ),
      };
    });

  return (
    <Tabs
      items={items}
      activeKey={activeKey}
      onChange={(k) => setActiveKey(k as TabKey)}
      destroyOnHidden={false}
      className="custom-tabs"
    />
  );
};

export default TabSelectSupplier;
